import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, Observable, Subject, Subscription, take, takeUntil } from 'rxjs';
import {
  AnnouncementInvitedJobseeker,
  AnnouncementInviteResponseEnum,
  AnnouncementInviteViewStatusEnum,
  ApplicationMethodEnum,
} from '@hrra/common-models';
import { AppState } from 'src/app/root-store/state';
import { AnnouncementDetails } from 'src/app/announcement/shared/models/announcement-details-model';
import {
  loadInviteAnnouncement,
  markInviteViewed,
  respondToInvite,
} from 'src/app/root-store/jobseeker-store/jobseeker-applies/actions';
import {
  selectAnnouncementInvitedJobseeker,
  selectAnnouncementInvitedJobseekerId,
  selectAnnouncementInvitedJobseekerMetaData,
  selectInviteAnnouncementDetails,
  selectInviteAnnouncementIsLoading,
  selectInviteAnnouncementNotFound,
  selectInviteAnnouncementPublishDate,
  selectIsJobseekerInvited,
} from 'src/app/root-store/jobseeker-store/jobseeker-applies/selectors';
import {
  selectCurrentLanguage,
  selectTimeZone,
} from 'src/app/root-store/selectors';
import { ErrorTypeEnum } from '@hrra/ui';
import { MetaService } from 'src/app/shared/services/meta.service';
import { DialogService } from '../../shared/services/dialog.service';

@Component({
  selector: 'app-jobseeker-invite-details',
  templateUrl: './jobseeker-invite-details.component.html',
  styleUrls: ['./jobseeker-invite-details.component.scss'],
  standalone: false,
})
export class JobseekerInviteDetailsComponent implements OnInit, OnDestroy {
  public announcementId: number;

  public announcementDetails$: Observable<AnnouncementDetails>;
  public announcementInvitedJobseeker$: Observable<AnnouncementInvitedJobseeker | null>;
  public inviteId$: Observable<number | undefined>;
  public isJobseekerInvited$: Observable<boolean | null | undefined>;
  public announcementNotFound$: Observable<boolean>;
  public publishDate$: Observable<Date>;
  public isLoading$: Observable<boolean>;
  public timeZone$: Observable<string>;
  public currentLanguage$: Observable<string>;

  public metaDataSubscription: Subscription;

  public viewStatusEnum = AnnouncementInviteViewStatusEnum;
  public errorType = ErrorTypeEnum;

  constructor(
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private dialogService: DialogService
  , public metaService: MetaService) {}

  ngOnInit(): void {
    this.announcementId = Number(
      this.route.snapshot.paramMap.get('announcementId')
    );

    this.store.dispatch(
      loadInviteAnnouncement({
        payload: { announcementId: this.announcementId },
      })
    );

    this.announcementDetails$ = this.store.select(
      selectInviteAnnouncementDetails
    );
    this.announcementInvitedJobseeker$ = this.store.select(
      selectAnnouncementInvitedJobseeker
    );
    this.isJobseekerInvited$ = this.store.select(selectIsJobseekerInvited);
    this.announcementNotFound$ = this.store.select(
      selectInviteAnnouncementNotFound
    );
    this.inviteId$ = this.store.select(selectAnnouncementInvitedJobseekerId);
    this.publishDate$ = this.store.select(selectInviteAnnouncementPublishDate);
    this.isLoading$ = this.store.select(selectInviteAnnouncementIsLoading);
    this.timeZone$ = this.store.select(selectTimeZone);
    this.currentLanguage$ = this.store.select(selectCurrentLanguage);

    this.announcementInvitedJobseeker$
      .pipe(
        filter((jobseeker): jobseeker is AnnouncementInvitedJobseeker => jobseeker != null),
        take(1)
      )
      .subscribe((announcementInvitedJobseeker) => {
        const inviteId = announcementInvitedJobseeker.announcementInvitedJobseekerId
        const isNew = announcementInvitedJobseeker.viewStatus === this.viewStatusEnum.New

        if(isNew){
          this.store.dispatch(
            markInviteViewed({
              payload: { inviteId },
            })
          );
        }
      });

      this.metaDataSubscription = this.store.select(selectAnnouncementInvitedJobseekerMetaData)
          .subscribe((data) => {
            this.metaService.updateMetaData(data?.website, data?.metaData);
      });
  }

  ngOnDestroy() {
    if (this.metaDataSubscription) {
      this.metaDataSubscription.unsubscribe();
    }
  }

  public onInviteRespond(response: {responseStatus: AnnouncementInviteResponseEnum, inviteId: number, applicationMethod: ApplicationMethodEnum}): void {
      this.dialogService
        .openInviteRespondConfirmDialog(response.responseStatus, response.applicationMethod)
        .afterClosed()
        .subscribe((confirmed) => {
          if (confirmed) {
            this.store.dispatch(
              respondToInvite({
                payload: { inviteId: response.inviteId, responseStatus: response.responseStatus },
              })
            );
          }
        });
  }
}
